import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ParkingType } from '../../../../core/models/parking/parking-type.model';
import { ApiParkingTypeService } from '../../../../core/services/api/parking/api-parking-type.service';
import { ModalParkingTypeComponent } from '../../modals/parking/parking-type/modal-parking-type/modal-parking-type.component';

@Component({
  selector: 'app-parking-type',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingTypeComponent],
  templateUrl: './parking-type.component.html',
  styleUrl: './parking-type.component.css'
})
export class ParkingTypeComponent implements OnInit {
  parkingTypes: ParkingType[] = [];
  isLoading: boolean = false;
  Math = Math;

  // Search and Filters
  searchText: string = '';
  filterVehicleType: string = ''; // '' = all
  filterOnline: string = ''; // '' = all, 'true' = online, 'false' = offline

  // Selection
  selectedIds: Set<number> = new Set<number>();

  // Modal State
  isModalOpen: boolean = false;
  selectedItem: ParkingType | null = null;

  // Dashboard Stats
  dashboardStats: any[] = [];

  // Data mapping for types
  vehicleTypeMap: { [key: string]: string } = {
    'รถยนต์': 'directions_car',
    'จักรยานยนต์': 'two_wheeler'
  };

  // Pagination
  currentPage: number = 1;
  pageSize: number = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems: number = 0;

  // Sorting
  sortColumn: string = 'id';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private apiParkingTypeService: ApiParkingTypeService,
    private toast: ToastService
  ) { }

  ngOnInit() {
    this.getParkingTypes();
  }

  getParkingTypes(): void {
    this.isLoading = true;
    this.apiParkingTypeService.getParkingTypes().subscribe({
      next: (res) => {
        try {
          if (res && Array.isArray(res)) {
            this.parkingTypes = res;
          } else {
            this.parkingTypes = [
              { id: 1, name: 'ลานจอดรถยนต์', vehicleType: 'รถยนต์', isOnline: true },
              { id: 2, name: 'ลานจอดรถมอเตอร์ไซค์', vehicleType: 'จักรยานยนต์', isOnline: true },
              { id: 3, name: 'ลานจอดช่องจอด EV', vehicleType: 'รถยนต์', isOnline: false }
            ];
          }
          this.totalItems = this.parkingTypes.length;
          this.calculateDashboardStats();
        } catch (error) {
          this.toast.error('โครงสร้างข้อมูลไม่ถูกต้อง');
        } finally {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error(err);
        this.parkingTypes = [
          { id: 1, name: 'ลานจอดรถยนต์', vehicleType: 'รถยนต์', isOnline: true },
          { id: 2, name: 'ลานจอดรถมอเตอร์ไซค์', vehicleType: 'จักรยานยนต์', isOnline: true },
          { id: 3, name: 'ลานจอดช่องจอด EV', vehicleType: 'รถยนต์', isOnline: false }
        ];
        this.totalItems = this.parkingTypes.length;
        this.calculateDashboardStats();
        this.isLoading = false;
      }
    });
  }

  private calculateDashboardStats(): void {
    const total = this.parkingTypes.length;
    const isOnlineTotal = this.parkingTypes.filter(p => p.isOnline).length;
    const carsTotal = this.parkingTypes.filter(p => p.vehicleType === 'รถยนต์').length;
    const motorcycleTotal = this.parkingTypes.filter(p => p.vehicleType === 'จักรยานยนต์').length;

    this.dashboardStats = [
      { title: 'ประเภทลานจอดทั้งหมด', value: total.toLocaleString(), icon: 'local_parking', color: 'text-blue-600' },
      { title: 'ออนไลน์', value: isOnlineTotal.toLocaleString(), icon: 'cloud_done', color: 'text-emerald-500' },
      { title: 'รถยนต์', value: carsTotal.toLocaleString(), icon: 'directions_car', color: 'text-amber-500' },
      { title: 'จักรยานยนต์', value: motorcycleTotal.toLocaleString(), icon: 'two_wheeler', color: 'text-indigo-500' }
    ];
  }

  get filteredAndPaginatedData(): ParkingType[] {
    let filtered = this.parkingTypes;

    // Dropdown Filters
    if (this.filterVehicleType) {
      filtered = filtered.filter(p => p.vehicleType === this.filterVehicleType);
    }

    if (this.filterOnline !== '') {
      const isOnline = this.filterOnline === 'true';
      filtered = filtered.filter(p => p.isOnline === isOnline);
    }

    // Search Box
    if (this.searchText.trim()) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(p =>
        (p.name?.toLowerCase().includes(search)) ||
        (p.vehicleType?.toLowerCase().includes(search)) ||
        (p.id.toString() === search)
      );
    }

    if (this.sortColumn) {
      filtered = [...filtered].sort((a: any, b: any) => {
        let valA = a[this.sortColumn as keyof ParkingType];
        let valB = b[this.sortColumn as keyof ParkingType];

        if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase() as any;
          valB = valB.toLowerCase() as any;
        }

        if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    this.totalItems = filtered.length;
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return filtered.slice(startIndex, startIndex + this.pageSize);
  }

  public onSearch(): void {
    this.currentPage = 1;
  }

  public onRefresh(): void {
    this.searchText = '';
    this.filterVehicleType = '';
    this.filterOnline = '';
    this.selectedIds.clear();
    this.isVehicleTypeDropdownOpen = false;
    this.isOnlineDropdownOpen = false;
    this.currentPage = 1;
    this.getParkingTypes();
  }

  // --- Dropdown Filters Logic ---
  isVehicleTypeDropdownOpen: boolean = false;
  isOnlineDropdownOpen: boolean = false;

  public toggleVehicleTypeDropdown(): void {
    this.isVehicleTypeDropdownOpen = !this.isVehicleTypeDropdownOpen;
    if (this.isVehicleTypeDropdownOpen) this.isOnlineDropdownOpen = false;
  }

  public toggleOnlineDropdown(): void {
    this.isOnlineDropdownOpen = !this.isOnlineDropdownOpen;
    if (this.isOnlineDropdownOpen) this.isVehicleTypeDropdownOpen = false;
  }

  public selectVehicleType(val: string): void {
    this.filterVehicleType = val;
    this.isVehicleTypeDropdownOpen = false;
    this.onSearch();
  }

  public selectOnline(val: string): void {
    this.filterOnline = val;
    this.isOnlineDropdownOpen = false;
    this.onSearch();
  }

  // --- Selection Logic ---
  public toggleSelection(id: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  public isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  public toggleAllSelection(): void {
    const isChecked = !this.isAllSelected();
    if (isChecked) {
      this.filteredAndPaginatedData.forEach(p => this.selectedIds.add(p.id));
    } else {
      this.filteredAndPaginatedData.forEach(p => this.selectedIds.delete(p.id));
    }
  }

  public isAllSelected(): boolean {
    if (this.filteredAndPaginatedData.length === 0) return false;
    return this.filteredAndPaginatedData.every(p => this.selectedIds.has(p.id));
  }

  // --- Modal Logic ---
  public navigateToCreate(): void {
    this.selectedItem = null;
    this.isModalOpen = true;
  }

  public closeModal(): void {
    this.isModalOpen = false;
    this.selectedItem = null;
  }

  public toggleOnline(item: ParkingType): void {
    item.isOnline = !item.isOnline;
  }

  public sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  // --- Pagination Helpers ---
  get totalPages(): number {
    return Math.ceil(this.totalItems / (this.pageSize || 1));
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  public setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  public onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize = Number(element.value);
    this.currentPage = 1;
  }

  public trackById(index: number, item: ParkingType): number {
    return item.id;
  }
}
